import { log } from "@/core/logger";
import { fetchWithAuth } from "@/utils/fetchWithAuth";
import { API_BASE_URL } from "./helpers";
import type { FeatureFlags, ISettingsAPI, MaintenanceStatus, Settings } from "./settingsApi";

/**
 * Remote API for Settings via HTTP
 */
export class RemoteSettingsAPI implements ISettingsAPI {
  /**
   * Get system settings from the backend
   */
  async getSettings(): Promise<Settings> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/settings`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.settings as Settings;
    } catch (error) {
      log("remoteSettingsApi", "Failed to fetch settings", error);
      throw error;
    }
  }

  /**
   * Update system settings
   */
  async updateSettings(updates: Partial<Omit<Settings, "id">>): Promise<Settings> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/settings`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.settings as Settings;
    } catch (error) {
      log("remoteSettingsApi", "Failed to update settings", error);
      throw error;
    }
  }

  /**
   * Get maintenance status (authenticated)
   */
  async getMaintenanceStatus(): Promise<MaintenanceStatus> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/settings/maintenance-status`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        // Fail open - return false on error
        return { isMaintenanceMode: false, maintenanceMessage: null };
      }

      return (await response.json()) as MaintenanceStatus;
    } catch (error) {
      log("remoteSettingsApi", "Failed to fetch maintenance status", error);
      // Fail open - don't block users if the check fails
      return { isMaintenanceMode: false, maintenanceMessage: null };
    }
  }

  /**
   * Get maintenance status (public - no auth required)
   */
  async getPublicMaintenanceStatus(): Promise<MaintenanceStatus> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/maintenance-status/public`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        // Fail open - return false on error
        return { isMaintenanceMode: false, maintenanceMessage: null };
      }

      return (await response.json()) as MaintenanceStatus;
    } catch (error) {
      log("remoteSettingsApi", "Failed to fetch public maintenance status", error);
      // Fail open - don't block users if the check fails
      return { isMaintenanceMode: false, maintenanceMessage: null };
    }
  }

  /**
   * Get runtime feature flags (authenticated; readable by any role).
   */
  async getFeatureFlags(): Promise<FeatureFlags> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/settings/features`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        // Fail closed — a feature flag defaults OFF if the read fails. (disabledReports fails OPEN:
        // hide nothing so a failed read never blanks the report picker.)
        return {
          earlySlotStartsEnabled: false,
          messagesEnabled: false,
          changeBookingDateTimeEnabled: false,
          changeBookingDurationEnabled: false,
          changeBookingRoomEnabled: false,
          upgradeToSlotEnabled: false,
          disabledReports: [],
        };
      }

      return (await response.json()) as FeatureFlags;
    } catch (error) {
      log("remoteSettingsApi", "Failed to fetch feature flags", error);
      return {
        earlySlotStartsEnabled: false,
        messagesEnabled: false,
        changeBookingDateTimeEnabled: false,
        changeBookingDurationEnabled: false,
        changeBookingRoomEnabled: false,
        upgradeToSlotEnabled: false,
        // Fail OPEN for report visibility: on a read failure hide nothing rather than blank the picker.
        disabledReports: [],
      };
    }
  }
}
